// Core
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { 
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
  Select,
  Grid,
  Text,
  useColorMode,
  useDisclosure,
  useColorModeValue
} from '@chakra-ui/react';
import { useQuery } from 'react-query';

// Assets
import { ReactComponent as ImagePlaceholder } from 'assets/svg/image-placeholder.svg';
import logoImage from 'assets/img/logo-3d.png';
import { apiService } from 'services/ApiService';

// Components
import { BreadcrumbArrowIcon } from 'components/Icons/Icons';
import PendingDeletion from './PendingDeletion';
import ContinueSaving from './ContinueSaving';
import ReasonForCancelling from './ReasonForCancelling';
import StatesModal from './modals/StatesModal';

const CancelSubscription = ({ 
  userData, 
  isOpen, 
  onClose, 
  onOpen, 
  confirmHandleCancelMembership,
  changeStatusSubItem,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const { isOpen: isOpenPreNextStep, onClose: onClosePreNextStep, onOpen: onOpenPreNextStep } = useDisclosure();

  const [step, setStep] = useState(1);
  const [reasonRadio, setReasonRadio] = useState();
  const [reasonRadioOther, setReasonOtherRadio] = useState();
  const [buttonReasonAndCancelMembership, setButtonReasonAndCancelMembership] = useState(false);

  const scrollToTop = () => {
    const topEl = document.getElementById('modalTop');
    topEl?.scrollIntoView({ behavior: 'auto', block: 'start' });
  };

  const backButton = async () => {
    if(step === 1){
      onClose();
    } else if(step === 2){
      setStep(1);
    } else if(step === 3){
      setStep(2);
    }

    scrollToTop();
    setButtonReasonAndCancelMembership(false);
  };

  const nextButton = async () => {
    if(step === 1){
      setStep(2);
    } else if(step === 2 && !isOpenPreNextStep){
      onOpenPreNextStep();
    } else if(step === 2 && isOpenPreNextStep){
      onClosePreNextStep();
      setStep(3);
    } 

    scrollToTop();
    setButtonReasonAndCancelMembership(false);
  };

  const sendReasonAndCancelMembership = () => {
      setButtonReasonAndCancelMembership(true);
      confirmHandleCancelMembership({
        reason: reasonRadio === 'Other' ? reasonRadioOther?.target?.value ?? 'Other'  : reasonRadio
      });
  };

  const { data: activePlans } = useQuery({
      queryKey: ['active-plans'],
      queryFn: async () => {
          const response = await apiService.getSubscriptions({status: 'active'});
          return response.data;
      },
      refetchOnWindowFocus: false
  });

  return (
  	<> 
  	<Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent 
      	className="modal__simple modal__simple_full-screen" 
        style={{borderRadius: '0 !important'}}>
        <ModalBody>
          <Box id="modalTop" mb="40px" />

          <Button 
            variant="secondary-md" 
            w="118px" 
            onClick={backButton}
            leftIcon={
              <BreadcrumbArrowIcon 
                fontSize="24px" 
                style={{
                  transform: 'rotate(180deg)',
                  marginLeft: '-12px'
                }}
                />
            }
          >Back</Button>

          <Image src={logoImage} 
            m={{ base: '32px auto 24px auto', md: '12px auto 48px auto'}}
            w={{ base: '154px', md: '268px'}} 
            h={{ base: '30px', md: '52px'}} />

          { step === 1 && (
            <PendingDeletion 
              userData={userData} 
              isDark={isDark} 
              activePlans={activePlans}
              changeStatusSubItem={changeStatusSubItem} />
          )}

          { step === 2 && (
            <ContinueSaving 
              userData={userData} 
              isDark={isDark} 
              onClose={onClose} 
              setStep={setStep}
              changeStatusSubItem={changeStatusSubItem}
              />
          )}

          <StatesModal 
            isOpen={isOpenPreNextStep}
            onClose={onClosePreNextStep}
            onOpen={onOpenPreNextStep}
            isDark={isDark}
            changeStatusSubItem={changeStatusSubItem}
            nextButton={nextButton}
            />

          { step === 3 && (
            <ReasonForCancelling 
              userData={userData} isDark={isDark}
              reasonRadio={reasonRadio}
              setReasonRadio={setReasonRadio}
              reasonRadioOther={reasonRadioOther}
              setReasonOtherRadio={setReasonOtherRadio}
              changeStatusSubItem={changeStatusSubItem}
              activePlans={activePlans}
              />
          )}

          <Box m="0 auto" maxW="346px" w="100%">
            <Button mt="64px" onClick={() => {onClose(); setStep(1);}} variant={ isDark ? 'button-lg-g' : 'button-lg'}>I don`t want to cancel, Return to profile</Button>
            { step !== 3 ?
              <Button mt="12px" onClick={nextButton} variant="delete-md">Proceed with Cancelling Plan</Button>
            :
              <Button mt="12px" 
                variant="delete-md" 
                disabled={reasonRadio ? false : true}
                isLoading={buttonReasonAndCancelMembership}
                onClick={sendReasonAndCancelMembership}>Confirm Plan Cancellation</Button>
            }
          </Box>

          <Box h="80px" />
        </ModalBody>
      </ModalContent>
    </Modal> 
    </>
  );
};

export default CancelSubscription;